'use client'

import { useMemo } from 'react'
import { Lock, MinusCircle, ChevronDown } from 'lucide-react'
import type { ActionItem, ToolKey, ToolState } from '@/types/radar'
import { TOOLS } from '@/types/radar'
import { EFFORT_LABELS } from '@/lib/radar/scoring'
import {
  buildProjection,
  type Projection,
  type RecoverableAction,
} from '@/lib/radar/projection'
import type { TierOneKey } from '@/lib/platform/derive-tool-scores'
import { Chip, InstrumentLabel, Panel } from './ui'

/**
 * PriorityRoadmap — "here is your highest-value next fix and what it recovers."
 *
 * Opportunity-forward by design. This is NOT a cost-of-inaction pitch: the
 * audience is a paying customer who already converted, and the useful thing
 * for them is a ranked worklist, not a fear forecast.
 *
 * Every number comes from `buildProjection`. This component performs NO
 * scoring or attribution arithmetic — it sorts for display and formats. If a
 * figure is needed that the projection does not expose, it belongs in
 * projection.ts with a test, never inline here.
 *
 * Renders only when the parity gate passes. A withheld crawl or a
 * non-gradeable readiness withholds the whole section rather than drawing a
 * roadmap from data that could not be measured.
 */

const TOOL_NAMES: Record<string, string> = Object.fromEntries(
  TOOLS.map(t => [t.key, t.name])
)

function toolName(key: string): string {
  return TOOL_NAMES[key] || key
}

/**
 * Rubric category names arrive in whatever shape their tool uses: prose
 * ("Foundational Coverage") from the scoreBreakdown[] tools, camelCase object keys
 * ("llmCommunication") from the categories{} tools. Humanize the latter rather
 * than leaking an internal identifier into a paying customer's worklist.
 */
const ACRONYMS = new Set(['llm', 'llms', 'ai', 'aeo', 'geo', 'url', 'urls'])

function categoryLabel(name: string): string {
  if (/\s/.test(name)) return name
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(' ')
    .map(word =>
      ACRONYMS.has(word.toLowerCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(' ')
}

/** Section label. A span, never a heading — these sit in flex rows with icons. */
function RoadmapLabel({
  children,
  count,
  swatch,
  className,
}: {
  children: React.ReactNode
  count?: string
  swatch?: string
  className?: string
}) {
  return (
    <InstrumentLabel
      tone={swatch ? 'inherit' : 'secondary'}
      className={className}
      style={swatch ? { color: swatch } : undefined}
      trailing={
        count ? (
          <span className='ml-1 font-mono text-2xs text-instrument'>
            {count}
          </span>
        ) : undefined
      }
    >
      {children}
    </InstrumentLabel>
  )
}

function EffortChip({ effort }: { effort: ActionItem['effort'] }) {
  const meta = EFFORT_LABELS[effort]
  return <Chip color={meta.color}>{meta.label}</Chip>
}

/**
 * Static gap bar on the aggregate infrastructure-score scale — the SAME 0-100
 * scale as the header figure. Three anchors: where you are, where the quick
 * wins alone land you, and the floor if every listed fix ships.
 *
 * Deliberately not a chart: no time axis, no month labels, no trajectory. A
 * rising line over months would read as a forecast, and we do not make one.
 * This shows a gap, and gaps have no time dimension.
 */
function GapBar({
  current,
  quickWin,
  ceiling,
}: {
  current: number
  quickWin: number
  ceiling: number
}) {
  const clamp = (n: number) => Math.max(0, Math.min(100, n))
  const cur = clamp(current)
  const qw = clamp(quickWin)
  const ceil = clamp(ceiling)

  return (
    <div className='mt-3'>
      <div
        className='relative w-full h-3 bg-muted'
        role='img'
        aria-label={`Infrastructure score ${current} of 100 today. Quick wins alone reach ${quickWin}. Shipping every listed fix reaches at least ${ceiling}.`}
      >
        {/* everything the listed fixes reach — the floor, drawn faintly */}
        <div
          className='absolute inset-y-0 left-0 bg-growth/20'
          style={{ width: `${ceil}%` }}
        />
        {/* quick wins alone */}
        <div
          className='absolute inset-y-0 left-0 bg-growth/40'
          style={{ width: `${qw}%` }}
        />
        {/* today */}
        <div
          className='absolute inset-y-0 left-0 bg-foreground/70'
          style={{ width: `${cur}%` }}
        />
        {/* ceiling target marker */}
        <div
          className='absolute inset-y-0 w-0.5 bg-growth'
          style={{ left: `calc(${ceil}% - 1px)` }}
        />
      </div>
      <div className='relative w-full mt-1 h-8 text-2xs text-muted-foreground'>
        <span className='absolute left-0 top-0 whitespace-nowrap'>
          <span className='block font-bold tabular-nums text-foreground'>
            {current}
          </span>
          now
        </span>
        <span
          className='absolute top-0 whitespace-nowrap -translate-x-1/2 text-center'
          style={{ left: `${qw}%` }}
        >
          <span className='block font-bold tabular-nums text-foreground/80'>
            {quickWin}
          </span>
          quick wins
        </span>
        <span className='absolute right-0 top-0 whitespace-nowrap text-right'>
          <span className='block font-bold tabular-nums text-growth'>
            at least {ceiling}
          </span>
          all fixes below
        </span>
      </div>
    </div>
  )
}

/** "+20 Crawl Check · +24 llms.txt Validator" — a shared fix, shown once. */
function PerToolRecovery({ action }: { action: RecoverableAction }) {
  return (
    <span className='text-[11px] text-muted-foreground'>
      {action.perTool.map((a, i) => (
        <span key={a.tool}>
          {i > 0 && <span className='mx-1 text-border'>·</span>}
          <span className='tabular-nums text-foreground/70'>
            +{a.scorePoints}
          </span>{' '}
          {toolName(a.tool)}
        </span>
      ))}
    </span>
  )
}

export function PriorityRoadmap({
  tools,
  actions,
  userTier,
  projection: suppliedProjection,
}: {
  tools?: Record<ToolKey, ToolState>
  actions: ActionItem[]
  userTier?: 'free' | 'paid'
  projection?: Projection | null
}) {
  const projection: Projection | null = useMemo(() => {
    if (suppliedProjection) return suppliedProjection
    if (!tools) return null
    const results: Partial<Record<TierOneKey, unknown>> = {}
    for (const key of [
      'crawl',
      'robots',
      'llms',
      'readiness',
      'aeo',
      'schemaAudit',
    ] as TierOneKey[]) {
      // `tools` is Record<ToolKey, ToolState> — every key is always present.
      // No optional chaining here on purpose: a missing entry is a bug in the
      // caller, and a `?.` would silently turn it into "tool not gradeable",
      // withholding the section for a reason nobody could trace.
      const state = tools[key as ToolKey]
      results[key] = state.status === 'done' ? state.data : null
    }
    return buildProjection(results, actions)
  }, [tools, actions, suppliedProjection])

  // Canonical order, shared with every other action surface. This used to
  // re-sort by effort-then-recovery, which disagreed with both the
  // projection's own order and the actions panel — three surfaces, three
  // orders, same underlying list. The roadmap now presents what the
  // projection already ranked.
  const sequenced = useMemo(
    () => (projection ? [...projection.rankedActions] : []),
    [projection]
  )

  if (userTier !== 'paid') return null
  if (!projection || !projection.ok) return null

  // Hero = the biggest ABSOLUTE recovery, which is what the "Biggest unlock"
  // label claims. It used to be sequenced[0] because that array was sorted
  // by recovery; now the table carries canonical EXECUTION order, so the top
  // row is the best first move and not necessarily the biggest unlock.
  // Keeping sequenced[0] here would have put a 3-point action under a label
  // that says "biggest". The tradeoff the previous comment flagged — hero
  // and table row 1 can now differ — is taken deliberately in exchange for
  // the label being true.
  const top = projection.topAction
  if (!top) return null

  const gatedByAction = new Map<string, typeof projection.gatedCategories>()
  for (const g of projection.gatedCategories) {
    gatedByAction.set(g.unlockedByActionId, [
      ...(gatedByAction.get(g.unlockedByActionId) || []),
      g,
    ])
  }

  return (
    <Panel aria-labelledby='priority-roadmap-heading'>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className='flex flex-col gap-3 border-b border-hairline px-6 py-6 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4 sm:px-7'>
        <div className='flex flex-col gap-1'>
          <InstrumentLabel tone='muted'>Roadmap</InstrumentLabel>
          <h3
            id='priority-roadmap-heading'
            className='font-display text-[25px] font-semibold leading-snug tracking-[-0.02em]'
          >
            Priority roadmap
          </h3>
        </div>
        {/* Framing requirement: this aggregate is NOT the dashboard's unified
            score. It is stated where the number is read, not in a tooltip. */}
        <span className='text-[13px] text-muted-foreground sm:text-right'>
          Infrastructure score{' '}
          <span className='font-semibold tabular-nums text-foreground'>
            {projection.infrastructureScore}
          </span>
          /100 &middot; the {projection.gradeableToolCount} audit tools only,
          not your overall score
        </span>
      </div>

      {/* ── Biggest unlock ─────────────────────────────────────────────── */}
      <div className='flex flex-col gap-4 border-b border-hairline px-6 py-6 sm:px-7'>
        <RoadmapLabel>Biggest unlock</RoadmapLabel>

        <div className='flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6'>
          <div className='flex min-w-0 flex-col gap-2'>
            <span className='font-display text-xl font-semibold leading-snug tracking-[-0.02em]'>
              {top.title}
            </span>
            <div className='flex flex-wrap items-center gap-x-3 gap-y-1.5'>
              <span
                className='text-sm font-semibold tabular-nums'
                style={{ color: 'var(--growth)' }}
              >
                Recovers at least +{top.scorePoints} tool points
              </span>
              <span className='text-sm tabular-nums text-muted-foreground'>
                &asymp; +{top.scoreDelta} infrastructure score
              </span>
              <PerToolRecovery action={top} />
            </div>
          </div>
          <span className='shrink-0'>
            <EffortChip effort={top.effort} />
          </span>
        </div>

        <GapBar
          current={projection.infrastructureScore}
          quickWin={projection.infrastructureQuickWin}
          ceiling={projection.infrastructureCeiling}
        />
      </div>

      {/* ── The table: the primary artifact ─────────────────────────────── */}
      {/* CSS grid, but with the table roles restored. The reference lays this
          out with `display: grid`; a ranked list of fixes with column headers
          is still a data table to a screen reader, and dropping to bare divs
          would have removed that. */}
      <div
        role='table'
        aria-label='Next fixes'
        className='flex flex-col border-b border-hairline px-6 py-5 sm:px-7'
      >
        <div
          role='row'
          className='grid grid-cols-1 gap-3 border-b border-hairline-strong pb-2.5 sm:grid-cols-[1fr_130px_130px]'
        >
          <span
            role='columnheader'
            className='instrument-label text-instrument'
          >
            Next fixes &middot; {sequenced.length}
          </span>
          <span
            role='columnheader'
            className='instrument-label hidden text-instrument sm:block'
          >
            Effort
          </span>
          <span
            role='columnheader'
            className='instrument-label hidden text-right text-instrument sm:block'
          >
            Recovers
          </span>
        </div>

        {sequenced.map(action => {
          const gated = gatedByAction.get(action.actionId)
          const gatedTotal = (gated || []).reduce((sum, g) => sum + g.points, 0)
          return (
            <div
              key={action.actionId}
              role='row'
              className='grid grid-cols-1 items-start gap-x-3 gap-y-2 border-b border-hairline py-3.5 last:border-b-0 sm:grid-cols-[1fr_130px_130px] sm:items-center'
            >
              <div role='cell' className='flex min-w-0 flex-col gap-1'>
                <span className='text-sm'>{action.title}</span>
                <PerToolRecovery action={action} />
                {gatedTotal > 0 && (
                  <span className='flex items-center gap-1.5'>
                    <Lock
                      size={12}
                      aria-hidden='true'
                      className='shrink-0 text-instrument'
                    />
                    <span className='text-[11px] leading-none text-instrument'>
                      unlocks a further{' '}
                      <span className='font-semibold tabular-nums text-muted-foreground'>
                        {Math.round(gatedTotal)}
                      </span>{' '}
                      points once shipped
                    </span>
                  </span>
                )}
              </div>

              <span role='cell' className='sm:whitespace-nowrap'>
                <EffortChip effort={action.effort} />
              </span>

              <div
                role='cell'
                className='flex items-baseline gap-2 sm:flex-col sm:items-end sm:gap-0.5'
              >
                <span
                  className='font-mono text-sm font-semibold tabular-nums'
                  style={{ color: 'var(--growth)' }}
                >
                  +{action.scorePoints}
                </span>
                <span className='font-mono text-[11px] tabular-nums text-instrument'>
                  &asymp; +{action.scoreDelta} score
                </span>
              </div>
            </div>
          )
        })}
      </div>

      {/* ── Methodology. The ceiling is a floor, not a cap. ──────────────── */}
      <div className='border-b border-hairline px-6 py-4 sm:px-7'>
        <span className='block max-w-[68ch] text-pretty text-[13px] leading-relaxed text-muted-foreground'>
          Tool points are that tool&apos;s own 0 to 100 rubric. Your
          infrastructure score is the average across{' '}
          {projection.gradeableToolCount} tools, so a fix moves it by roughly a{' '}
          {projection.gradeableToolCount}th of its tool points. Recovering all
          of the above lifts the infrastructure score to at least{' '}
          <span className='font-semibold tabular-nums text-foreground'>
            {projection.infrastructureCeiling}
          </span>
          . Derived checks re-score once the underlying fixes land, so real
          gains typically run higher.
        </span>
      </div>

      {/* ── Gated: real points, waiting on a named fix. Not in the reference,
            kept because these are earned points the customer can still reach
            and dropping them would hide recoverable score. ──────────────── */}
      {projection.gatedCategories.length > 0 && (
        <div className='flex flex-col gap-2.5 border-b border-hairline px-6 py-5 sm:px-7'>
          <RoadmapLabel
            count={`${Math.round(projection.totalGatedPoints)} points`}
          >
            Unlocked after the fixes above
          </RoadmapLabel>
          <ul className='flex flex-col gap-1.5'>
            {projection.gatedCategories.map(g => (
              <li
                key={`${g.tool}-${g.category}`}
                className='grid grid-cols-[1fr_auto] items-baseline gap-x-3 text-[13px]'
              >
                <span className='min-w-0 text-instrument'>
                  <span className='text-muted-foreground'>
                    {categoryLabel(g.category)}
                  </span>{' '}
                  &mdash; {toolName(g.tool)} &mdash; unlocked by:{' '}
                  {g.unlockedByTitle}
                </span>
                <span className='font-mono font-semibold tabular-nums text-muted-foreground'>
                  {Math.round(g.points)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Stranded: shown with the REAL triage reason, never a blank row.
            Collapsed by design — the transparency matters, but the card must
            lead with what the customer CAN do. ─────────────────────────── */}
      {projection.unfixedCategories.length > 0 && (
        <details className='group/stranded'>
          <summary className='flex cursor-pointer select-none items-center justify-between gap-3 px-6 py-3.5 transition-colors hover:bg-surface-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset sm:px-7'>
            <InstrumentLabel tone='secondary'>
              Not in the roadmap &middot;{' '}
              {Math.round(projection.totalStrandedPoints)} points, and why
            </InstrumentLabel>
            <ChevronDown
              size={14}
              aria-hidden='true'
              className='shrink-0 -rotate-90 text-muted-foreground transition-transform group-open/stranded:rotate-0'
            />
          </summary>
          <div className='border-t border-hairline px-6 pb-4 pt-4 sm:px-7'>
            <ul className='flex flex-col gap-2.5'>
              {projection.unfixedCategories.map(u => (
                <li
                  key={`${u.tool}-${u.category}`}
                  className='grid grid-cols-[auto_1fr_auto] items-baseline gap-x-2.5 text-[13px]'
                >
                  <MinusCircle
                    size={12}
                    aria-hidden='true'
                    className='translate-y-0.5 shrink-0 text-instrument'
                  />
                  <span className='min-w-0'>
                    <span className='text-muted-foreground'>
                      {categoryLabel(u.category)}
                    </span>
                    <span className='text-instrument'>
                      {' '}
                      &mdash; {toolName(u.tool)}
                    </span>
                    <span className='mt-0.5 block text-[11px] leading-snug text-instrument'>
                      {u.reason ?? 'No fix identified yet.'}
                    </span>
                  </span>
                  <span className='font-mono font-semibold tabular-nums text-instrument'>
                    {Math.round(u.remainingPoints)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </details>
      )}
    </Panel>
  )
}
